/**
 * Danh mục tham chiếu: địa điểm và ca làm việc.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CẢ HAI ĐỀU CACHE VÀO localStorage — BẮT BUỘC, KHÔNG PHẢI TỐI ƯU
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Công trường thường mất sóng. PWA vẫn phải biết:
 *   · nơi này có bắt buộc Face ID không → để bật camera TRƯỚC khi xếp hàng đợi
 *   · danh sách ca → để nhân viên chọn ca rồi vẫn xếp được vào hàng đợi
 *
 * Hỏi server lúc đó thì đã muộn. Hai danh sách này chỉ vài dòng và đổi vài
 * tháng một lần, nên cache cũ vài giờ không phải vấn đề.
 */

import { KHOA_FACE, request } from '../api/client'
import type { Ca, DiaDiem, TrangThaiFace } from '../types'

const CACHE_DIA_DIEM = 'chamcong.dia_diem'
const CACHE_CA = 'chamcong.ca_lam_viec'

function docCache<T>(khoa: string): T[] {
  try {
    const s = localStorage.getItem(khoa)
    return s ? (JSON.parse(s) as T[]) : []
  } catch {
    return []
  }
}

export async function layDiaDiem(): Promise<DiaDiem[]> {
  try {
    const kq = await request<{ items: DiaDiem[] }>('/locations')
    localStorage.setItem(CACHE_DIA_DIEM, JSON.stringify(kq.items))
    return kq.items
  } catch {
    return docCache<DiaDiem>(CACHE_DIA_DIEM)
  }
}

/**
 * Danh sách ca đang dùng.
 *
 * Server chỉ trả ca `is_active` — admin ngừng một ca là nó biến mất ở lần làm
 * mới tiếp theo trên mọi máy.
 */
export async function layCa(): Promise<Ca[]> {
  try {
    const kq = await request<{ items: Ca[] }>('/shifts')
    localStorage.setItem(CACHE_CA, JSON.stringify(kq.items))
    return kq.items
  } catch {
    return docCache<Ca>(CACHE_CA)
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * KHOẢNG CÁCH VÀ GEOFENCE
 * ═══════════════════════════════════════════════════════════════════════════ */

function khoangCachMet(
  lat1: number, lng1: number, lat2: number, lng2: number,
): number {
  const R = 6_371_000
  const rad = (d: number) => (d * Math.PI) / 180
  const p1 = rad(lat1), p2 = rad(lat2)
  const dp = p2 - p1, dl = rad(lng2 - lng1)
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

async function trongGeofence(lat: number, lng: number): Promise<DiaDiem[]> {
  const ds = await layDiaDiem()
  return ds
    .map((d) => ({ d, m: khoangCachMet(lat, lng, d.lat, d.lng) }))
    .filter((x) => x.m <= x.d.radius_m)
    .sort((a, b) => a.m - b.m)
    .map((x) => x.d)
}

/**
 * Toạ độ này có rơi vào địa điểm bắt buộc Face ID không?
 *
 * Chỉ để QUYẾT ĐỊNH CÓ BẬT CAMERA HAY KHÔNG ở phía client. Server vẫn tự dò
 * lại địa điểm và tự cưỡng chế yêu cầu Face ID — client không được tin.
 *
 * Không khớp địa điểm nào (công trình mới chưa khai báo, docs/03 D7) thì VẪN
 * yêu cầu chụp mặt: mặc định an toàn, vì chấm công ngoài văn phòng đã biết
 * đều là công trình.
 */
export async function canFaceTaiViTri(lat: number, lng: number): Promise<boolean> {
  const ds = await layDiaDiem()
  if (ds.length === 0) return true      // chưa có dữ liệu → chọn phía an toàn

  const trong = await trongGeofence(lat, lng)
  if (trong.length === 0) return true   // ngoài mọi geofence → coi như công trình
  return trong[0].require_face
}

/**
 * Toạ độ này có nằm trong một VĂN PHÒNG không?
 *
 * Quyết định khung hình khi chụp: văn phòng chỉ cần chân dung, ngoài văn
 * phòng phải thấy cả công trình phía sau. Không biết (mất GPS, chưa có cache)
 * → coi như ngoài văn phòng, vì đó là phía cần nhiều bằng chứng hơn.
 */
export async function laVanPhong(lat: number, lng: number): Promise<boolean> {
  const trong = await trongGeofence(lat, lng)
  return trong.length > 0 && trong[0].kind === 'office'
}

/**
 * Công trình để nhân viên tự chọn khi máy không lấy được toạ độ.
 *
 * Đọc từ cache nên dùng được cả khi mất sóng — chính là lúc cần nó nhất.
 * Gần nhất lên đầu nếu biết vị trí; không biết thì theo tên.
 */
export async function layCongTrinh(
  gan?: { lat: number; lng: number } | null,
): Promise<DiaDiem[]> {
  const ds = (await layDiaDiem()).filter((d) => d.kind === 'site')
  if (!gan) return ds.sort((a, b) => a.name.localeCompare(b.name, 'vi'))
  return ds
    .map((d) => ({ d, m: khoangCachMet(gan.lat, gan.lng, d.lat, d.lng) }))
    .sort((a, b) => a.m - b.m)
    .map((x) => x.d)
}

/* ═══════════════════════════════════════════════════════════════════════════
 * TRẠNG THÁI FACE ID (đọc — phần ghi ở `face.service.ts`)
 * ═══════════════════════════════════════════════════════════════════════════ */

export async function layTrangThaiFace(): Promise<TrangThaiFace> {
  const kq = await request<TrangThaiFace>('/auth/face-status')
  localStorage.setItem(KHOA_FACE, JSON.stringify({
    da_dang_ky: kq.da_dang_ky, da_dong_y: kq.da_dong_y,
  }))
  return kq
}

/** Đã đăng ký khuôn mặt chưa, đọc từ cache. `null` = chưa biết (chưa từng tải). */
export function faceDaDangKyCache(): boolean | null {
  try {
    const s = localStorage.getItem(KHOA_FACE)
    return s ? Boolean((JSON.parse(s) as { da_dang_ky?: boolean }).da_dang_ky) : null
  } catch {
    return null
  }
}
